import {
	AddSubFormButtonRenderProps,
	DateTimeField,
	FormType,
	SubFormRenderProps,
	TextField,
} from "@hex-insights/forms";
import { CourseAssessmentSubmissionFormValues, MediaAttachmentFormValues } from "../../FormUtilities";
import { CourseAssessmentSelectField } from "../CourseAssessment";
import { MediaAttachmentCustomForm } from "../MediaAttachment";
import { BaseFieldProps } from "../shared";
import { StudentSelectField } from "../Student";
import { UserSelectField } from "../User";

/**
 * Generic props for fields of the CourseAssessmentSubmission model.
 */
type FieldProps<
	K extends keyof CourseAssessmentSubmissionFormValues.Base = keyof CourseAssessmentSubmissionFormValues.Base,
> = BaseFieldProps<Pick<CourseAssessmentSubmissionFormValues.Base, K>>;

/**
 * Generic props for fields of the CourseAssessmentSubmission model that only appear in the create form.
 */
type CreateFieldProps<
	K extends keyof CourseAssessmentSubmissionFormValues.Create = keyof CourseAssessmentSubmissionFormValues.Create,
> = BaseFieldProps<Pick<CourseAssessmentSubmissionFormValues.Create, K>>;

/**
 * Generic props for fields of the CourseAssessmentSubmission model that only appear in the detail form.
 */
type DetailFieldProps<
	K extends keyof CourseAssessmentSubmissionFormValues.Detail = keyof CourseAssessmentSubmissionFormValues.Detail,
> = BaseFieldProps<Pick<CourseAssessmentSubmissionFormValues.Detail, K>>;

/**
 * Field component for the `createdAt` field of the CourseAssessmentSubmission model.
 */
export function CreatedAt({ formState, ...props }: DetailFieldProps<"createdAt">) {
	return (
		<DateTimeField
			formState={formState}
			name="createdAt"
			optional={FormType.isCreate(formState.formType)}
			precision="minute"
			{...props}
		/>
	);
}

/**
 * Field component for the `body` field of the CourseAssessmentSubmission model.
 */
export function Body({ formState, ...props }: FieldProps<"body">) {
	return <TextField formState={formState} name="body" multiLine optional {...props} />;
}

/**
 * Field component for the `submissionDate` field of the CourseAssessmentSubmission model.
 */
export function SubmissionDate({ formState, ...props }: FieldProps<"submissionDate">) {
	return (
		<DateTimeField
			formState={formState}
			name="submissionDate"
			optional={FormType.isCreate(formState.formType)}
			precision="day"
			{...props}
		/>
	);
}

export namespace Author {
	export type SelectProps = DetailFieldProps<"authorID"> &
		Omit<UserSelectField.SelectProps<CourseAssessmentSubmissionFormValues.Detail>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `author` edge of the CourseAssessmentSubmission model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.Select formState={formState} name="authorID" optional {...props} />;
	}
}

export namespace CourseAssessment {
	export type SelectProps = FieldProps<"courseAssessmentID"> &
		Omit<
			CourseAssessmentSelectField.SelectProps<CourseAssessmentSubmissionFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `courseAssessment` edge of the CourseAssessmentSubmission model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <CourseAssessmentSelectField.Select formState={formState} name="courseAssessmentID" {...props} />;
	}
}

export namespace MediaAttachments {
	/**
	 * SubForm field component for the `mediaAttachments` edge of the CourseAssessmentSubmission model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"mediaAttachments">) {
		return (
			<MediaAttachmentCustomForm.SubForm
				formState={formState}
				name="mediaAttachments"
				blankItem={MediaAttachmentFormValues.blankItemInCourseAssessmentSubmission}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</MediaAttachmentCustomForm.SubForm>
		);
	}

	SubForm.SubForm = function (props: SubFormRenderProps<MediaAttachmentFormValues.CreateInCourseAssessmentSubmission>) {
		return (
			<MediaAttachmentCustomForm.SubForm.SubForm {...props}>
				<MediaAttachmentCustomForm.CreateFieldsInCourseAssessmentSubmission formState={props.formState} />
			</MediaAttachmentCustomForm.SubForm.SubForm>
		);
	};

	SubForm.AddButton = function (
		props: AddSubFormButtonRenderProps<
			Pick<CourseAssessmentSubmissionFormValues.Create, "mediaAttachments">,
			MediaAttachmentFormValues.CreateInCourseAssessmentSubmission
		>,
	) {
		return <MediaAttachmentCustomForm.SubForm.AddButton {...props} />;
	};
}

export namespace Student {
	export type SelectProps = FieldProps<"studentID"> &
		Omit<StudentSelectField.SelectProps<CourseAssessmentSubmissionFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `student` edge of the CourseAssessmentSubmission model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentSelectField.Select formState={formState} name="studentID" {...props} />;
	}
}
